import { useEffect, useState } from "react";
import { api, formatARS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, addDays, addWeeks, addMonths, addYears, isSameDay } from "date-fns";
import { es } from "date-fns/locale";

const VIEWS = ["dia", "semana", "mes", "ano"];

export default function Bookings() {
  const [view, setView] = useState("semana");
  const [cursor, setCursor] = useState(new Date());
  const [bookings, setBookings] = useState([]);

  const getRange = () => {
    if (view === "dia") return { from: cursor, to: cursor };
    if (view === "semana") return { from: startOfWeek(cursor, { weekStartsOn: 1 }), to: endOfWeek(cursor, { weekStartsOn: 1 }) };
    if (view === "mes") return { from: startOfMonth(cursor), to: endOfMonth(cursor) };
    return { from: startOfYear(cursor), to: endOfYear(cursor) };
  };

  const load = async () => {
    const { from, to } = getRange();
    const { data } = await api.get("/bookings", {
      params: {
        date_from: format(from, "yyyy-MM-dd"),
        date_to: format(to, "yyyy-MM-dd"),
      },
    });
    setBookings(data);
  };

  useEffect(() => { load(); }, [view, cursor]);

  const nav = (dir) => {
    const f = dir === 1 ? 1 : -1;
    if (view === "dia") setCursor(addDays(cursor, f));
    else if (view === "semana") setCursor(addWeeks(cursor, f));
    else if (view === "mes") setCursor(addMonths(cursor, f));
    else setCursor(addYears(cursor, f));
  };

  const confirmBooking = async (id) => {
    try {
      const { data } = await api.patch(`/bookings/${id}/confirm`);
      toast.success("Turno confirmado. Abriendo WhatsApp...");
      window.open(data.whatsapp_link, "_blank");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const cancelBooking = async (id) => {
    if (!window.confirm("¿Cancelar este turno?")) return;
    try {
      await api.patch(`/bookings/${id}/cancel`);
      toast.success("Turno cancelado");
      load();
    } catch (e) {
      toast.error("Error");
    }
  };

  const statusBadge = (s) => {
    const map = {
      pending: "bg-amber-500/10 text-amber-400 border-amber-500/30",
      confirmed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
      cancelled: "bg-red-500/10 text-red-400 border-red-500/30",
    };
    const labels = { pending: "Pendiente", confirmed: "Confirmado", cancelled: "Cancelado" };
    return <Badge className={`${map[s]} uppercase tracking-widest text-xs border`}>{labels[s]}</Badge>;
  };

  const { from, to } = getRange();
  const rangeLabel = view === "dia"
    ? format(cursor, "EEEE d 'de' MMMM yyyy", { locale: es })
    : view === "semana"
    ? `${format(from, "d MMM", { locale: es })} → ${format(to, "d MMM yyyy", { locale: es })}`
    : view === "mes"
    ? format(cursor, "MMMM yyyy", { locale: es })
    : format(cursor, "yyyy");

  // Group bookings by date
  const grouped = bookings.reduce((acc, b) => {
    (acc[b.date] = acc[b.date] || []).push(b);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort();

  return (
    <div>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-2">// Agenda</div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight">TURNOS</h1>
        </div>
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="bg-[#141414] border border-[#262626] flex-wrap h-auto">
            {VIEWS.map((v) => (
              <TabsTrigger key={v} value={v} className="uppercase tracking-widest text-xs data-[state=active]:bg-emerald-500 data-[state=active]:text-black" data-testid={`view-${v}`}>
                {v === "dia" ? "Día" : v === "semana" ? "Semana" : v === "mes" ? "Mes" : "Año"}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="flex items-center justify-between mb-6 bg-[#141414] border border-[#262626] rounded-lg p-4">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="text-zinc-400" data-testid="cal-prev-btn">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="font-heading text-xl tracking-wider capitalize" data-testid="cal-range">{rangeLabel}</div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setCursor(new Date())} className="uppercase tracking-widest text-xs text-zinc-400" data-testid="cal-today-btn">
            Hoy
          </Button>
          <Button variant="ghost" size="sm" onClick={() => nav(1)} className="text-zinc-400" data-testid="cal-next-btn">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {bookings.length === 0 ? (
        <div className="bg-[#141414] border border-[#262626] rounded-lg p-16 text-center text-zinc-500" data-testid="no-bookings">
          <Calendar className="h-12 w-12 mx-auto mb-4 text-zinc-700" />
          <div className="uppercase tracking-widest text-xs">Sin turnos en este período</div>
        </div>
      ) : (
        <div className="space-y-6">
          {sortedDates.map((date) => (
            <div key={date}>
              <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-3">
                {format(new Date(date + "T00:00"), "EEEE d 'de' MMMM", { locale: es })}
                {isSameDay(new Date(date + "T00:00"), new Date()) && " · HOY"}
              </div>
              <div className="space-y-2">
                {grouped[date].sort((a, b) => a.start_time.localeCompare(b.start_time)).map((b) => (
                  <div key={b.booking_id} className="bg-[#141414] border border-[#262626] rounded-lg p-5 hover:border-emerald-500/30 transition flex flex-col md:flex-row md:items-center gap-4" data-testid={`booking-${b.booking_id}`}>
                    <div className="font-heading text-3xl text-emerald-400 w-24">{b.start_time}</div>
                    <div className="flex-1">
                      <div className="font-bold">{b.customer_name}</div>
                      <div className="text-sm text-zinc-400">{b.court_name} · {b.customer_phone}</div>
                      {b.notes && <div className="text-xs text-zinc-500 mt-1">{b.notes}</div>}
                    </div>
                    <div className="text-right">
                      <div className="font-heading text-xl">{formatARS(b.price_ars)}</div>
                      {statusBadge(b.status)}
                    </div>
                    {b.status === "pending" && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => confirmBooking(b.booking_id)} className="bg-emerald-500 hover:bg-emerald-600 text-black uppercase tracking-widest text-xs font-bold" data-testid={`confirm-${b.booking_id}`}>
                          <MessageCircle className="h-3 w-3 mr-1" /> Confirmar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => cancelBooking(b.booking_id)} className="border-red-500/30 text-red-400 bg-transparent hover:bg-red-500/10" data-testid={`cancel-${b.booking_id}`}>
                          <XCircle className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                    {b.status === "confirmed" && (
                      <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Calendar(props) {
  // Fallback icon — using lucide import name collision protection
  return <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>;
}
